import React from 'react';
import styled, { css } from 'styled-components';

import {
  Bot,
  Award,
  Zap,
  Cpu,
  Rocket,
  Laptop,
  Database,
  Cloud,
  Server,
  Smartphone,
  Globe,
} from 'lucide-react';
import Header from '../../components/Header';
import QuizCard from '../../components/Quiz/quiz-card.component';
import { useQuizPage } from './quiz-page.hook';

export default function QuizPage() {
  const { studentQuizzes } = useQuizPage();

  const colors = [
    '#D1F7C4',
    '#A7D8FF',
    '#B6F8D9',
    '#E3D7FF',
    '#FEE2A0',
    '#93C5FD',
    '#A7F3D0',
    '#F9A8D4',
  ];

  const icons = [
    <RobotIconHighlighted />,
    <ZapIconHighlighted />,
    <CpuIconHighlighted />,
    <RocketIconHighlighted />,
    <LaptopIconHighlighted />,
    <DatabaseIconHighlighted />,
    <CloudIconHighlighted />,
    <ServerIconHighlighted />,
    <SmartphoneIconHighlighted />,
    <GlobeIconHighlighted />,
  ];

  return (
    <PageWrapper>
      <Header />
      <Main>
        <Section>
          <SectionTitle>Your Quizzes</SectionTitle>
          <QuizGrid>
            {studentQuizzes.map((quiz, index) => (
              <QuizCard
                key={quiz.id}
                title={quiz.title}
                attempts={quiz.attempts}
                description={quiz.description}
                icon={icons[index % icons.length]}
                color={colors[index % colors.length]}
                questionCount={quiz.questions.length}
                href={`/quiz/${quiz.id}/attempt`}
                doneAt={quiz.doneAt}
              />
            ))}
          </QuizGrid>
        </Section>
        {/* 
        <SectionCard>
          <SectionTitle>Your Achievements</SectionTitle>
          <AchievementsGrid>
            <AchievementCard bg="#FEF3C7">
              <AchievementIcon bg="#FEF9C3">
                <AwardIcon />
              </AchievementIcon>
              <AchievementText color="#B45309">
                3 Quizzes Completed
              </AchievementText>
            </AchievementCard>
            <AchievementCard bg="#D1FAE5">
              <AchievementIcon bg="#A7F3D0">
                <AwardIcon />
              </AchievementIcon>
              <AchievementText color="#065F46">
                12 Correct Answers
              </AchievementText>
            </AchievementCard>
            <AchievementCard bg="#DBEAFE">
              <AchievementIcon bg="#BFDBFE">
                <AwardIcon />
              </AchievementIcon>
              <AchievementText color="#1E40AF">Robot Expert</AchievementText>
            </AchievementCard>
            <AchievementCard bg="#EDE9FE">
              <AchievementIcon bg="#DDD6FE">
                <AwardIcon />
              </AchievementIcon>
              <AchievementText color="#5B21B6">2-Day Streak</AchievementText>
            </AchievementCard>
          </AchievementsGrid>
        </SectionCard> */}
      </Main>
    </PageWrapper>
  );
}

// Styled Components
const PageWrapper = styled.div`
  min-height: 100vh;
  background-color: ${({ theme }) => theme.colors.background};
`;

const HeaderContainer = styled.div`
  max-width: 768px;
  margin: 0 auto;
  padding: 0 1rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;

  @media (min-width: 768px) {
    flex-direction: row;
    justify-content: space-between;
  }
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const RobotIcon = styled(Bot)`
  width: 2rem;
  height: 2rem;
`;

const BrandName = styled.span`
  font-size: 1.875rem;
  font-weight: bold;
`;

const UserBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background-color: #ffffff;
  color: #7c3aed;
  border-radius: 9999px;
  padding: 0.5rem 1.5rem;
  font-weight: bold;
`;

const AwardIcon = styled(Award)`
  width: 1.5rem;
  height: 1.5rem;
`;

const Main = styled.main`
  width: 100%;
  margin: 0 auto;
  padding: 2rem 3rem;
`;

const Section = styled.section`
  margin-bottom: 2rem;
  width: 100%;
  /* background-color: green; */
`;

const SectionTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: bold;
  color: #6b21a8;
  margin-bottom: 1.5rem;
`;

const QuizGrid = styled.div`
  display: grid;
  gap: 1.5rem;
  width: 100%;
  grid-template-columns: repeat(
    auto-fill,
    minmax(250px, 1fr)
  ); /* Ajuste para flexibilidade */

  @media (min-width: 768px) {
    grid-template-columns: repeat(
      auto-fill,
      minmax(250px, 1fr)
    ); /* Colunas adaptáveis */
  }

  @media (min-width: 1024px) {
    grid-template-columns: repeat(
      2,
      1fr
    ); /* No máximo 3 colunas em telas maiores */
  }
`;

// Icon variations with colors
const RobotIconHighlighted = styled(Bot)`
  width: 2.5rem;
  height: 2.5rem;
  color: #fbbf24; // yellow-500
`;

const ZapIconHighlighted = styled(Zap)`
  width: 2.5rem;
  height: 2.5rem;
  color: #3b82f6; // blue-500
`;

const CpuIconHighlighted = styled(Cpu)`
  width: 2.5rem;
  height: 2.5rem;
  color: #10b981; // green-500
`;

const RocketIconHighlighted = styled(Rocket)`
  width: 2.5rem;
  height: 2.5rem;
  color: #a78bfa; // purple-500
`;

const LaptopIconHighlighted = styled(Laptop)`
  width: 2.5rem;
  height: 2.5rem;
  color: #fee2a0; // light yellow
`;

const DatabaseIconHighlighted = styled(Database)`
  width: 2.5rem;
  height: 2.5rem;
  color: #93c5fd; // light blue
`;

const CloudIconHighlighted = styled(Cloud)`
  width: 2.5rem;
  height: 2.5rem;
  color: #a7f3d0; // light green
`;

const ServerIconHighlighted = styled(Server)`
  width: 2.5rem;
  height: 2.5rem;
  color: #f9a8d4; // light pink
`;

const SmartphoneIconHighlighted = styled(Smartphone)`
  width: 2.5rem;
  height: 2.5rem;
  color: #e5e7eb; // light gray
`;

const GlobeIconHighlighted = styled(Globe)`
  width: 2.5rem;
  height: 2.5rem;
  color: #a7d8ff; // light blue
`;
const SectionCard = styled(Section)`
  background-color: #ffffff;
  border-radius: 1rem;
  padding: 1.5rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

const AchievementsGrid = styled.div`
  display: grid;
  gap: 1rem;
  grid-template-columns: repeat(2, 1fr);

  @media (min-width: 768px) {
    grid-template-columns: repeat(4, 1fr);
  }
`;

const AchievementCard = styled.div<{ bg: string }>`
  background-color: ${props => props.bg};
  border-radius: 1rem;
  padding: 1rem;
  text-align: center;
`;

const AchievementIcon = styled.div<{ bg: string }>`
  background-color: ${props => props.bg};
  border-radius: 50%;
  width: 4rem;
  height: 4rem;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 0.5rem;
`;

const AchievementText = styled.p<{ color: string }>`
  font-weight: bold;
  color: ${props => props.color};
`;
